/**
 * One place that turns a server timestamp into something a human reads.
 *
 * THE RULE, and it is the only one that matters:
 *
 *     store UTC · transport ISO-8601 WITH an offset · render in the viewer's zone
 *
 * UZO gets the first right and the third wrong, and the second is right about
 * 96% of the time — which is the dangerous number, because it means the same
 * screen shows some timestamps correctly and some shifted, and neither looks
 * wrong.
 *
 * ── WHAT WAS ACTUALLY MEASURED (2026-08-10) ─────────────────────────────────
 *
 * Storage is fine. The Postgres session is UTC and 664 of 665 datetime columns
 * in `maple` are `timestamptz`. Those serialise as `2026-08-10T03:19:45+00:00`
 * and `new Date()` reads them correctly.
 *
 * Transport is not fine. 42 backend sites emit `datetime.utcnow().isoformat()`,
 * which yields `2026-08-10T03:19:45` — no `Z`, no offset. Per ECMA-262 a
 * date-time string with no designator is parsed as **local time**, so a viewer
 * in CST sees those values shifted five or six hours (DST-dependent) while the
 * timestamptz values beside them are right.
 *
 * ── WHY PARSING IS THE HIGHEST-LEVERAGE FIX ─────────────────────────────────
 *
 * `parseServerDate` assumes UTC when a string carries no offset. That single
 * decision corrects every one of those 42 sites without touching them, and —
 * more importantly — it keeps them correct if someone writes `utcnow()` again
 * next month. The backend is being fixed too, but the backend fix is a
 * convention and this one is a guarantee. Prefer guarantees at boundaries.
 *
 * The inverse assumption (treat naive as local) is what the platform does today
 * by default, and it is never right: the server has no idea what the viewer's
 * clock says, so a naive string from a server is always UTC in intent.
 *
 * ── WHICH ZONE ──────────────────────────────────────────────────────────────
 *
 * `Intl.DateTimeFormat().resolvedOptions().timeZone` — the viewer's actual IANA
 * zone. A user in Chicago sees CST/CDT without configuring anything, and the
 * DST boundary is handled by the platform's own tz database rather than by an
 * offset we cached. Never store or pass a fixed offset; offsets are a property
 * of an instant, not of a place.
 *
 * An explicit override wins when set (see `setUserTimeZone`), for the real SaaS
 * case: a team that reports in one zone regardless of where a member is sitting.
 * `maple.users.timezone` exists for this and currently reads 'UTC' for all 82
 * users — a default nobody set, exposed by no endpoint and read by no screen.
 * It is deliberately NOT consulted here yet: treating that column as an
 * expressed preference would pin every user to UTC, which is the bug this file
 * exists to remove. Wire it when a settings control writes it.
 */
import { fmtDurationSec } from './monitor/format';

export type DateLike = string | number | Date | null | undefined;

const TZ_OVERRIDE_KEY = 'uzo.timezone';

/** A string that already states its offset: `Z`, `+05:30`, `-0600`. */
const HAS_OFFSET = /(?:[Zz]|[+-]\d{2}:?\d{2})$/;

/** `2026-08-10T03:19:45[.123456]` — ISO-shaped and silent about its zone. */
const NAIVE_ISO = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?$/;

/**
 * The viewer's IANA zone — an explicit override if one is set, else the
 * browser's. Returns 'UTC' during SSR, where there is no viewer to ask.
 */
export function userTimeZone(): string {
  try {
    if (typeof window !== 'undefined') {
      const override = window.localStorage.getItem(TZ_OVERRIDE_KEY);
      if (override) return override;
    }
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Pin every timestamp to one zone, or pass null to follow the browser again. */
export function setUserTimeZone(tz: string | null): void {
  try {
    if (tz) window.localStorage.setItem(TZ_OVERRIDE_KEY, tz);
    else window.localStorage.removeItem(TZ_OVERRIDE_KEY);
  } catch {
    /* private mode / SSR — fall back to the browser zone */
  }
}

/** Every IANA zone the runtime knows, for a picker. Falls back to a short list. */
export function availableTimeZones(): string[] {
  try {
    const anyIntl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
    const all = anyIntl.supportedValuesOf?.('timeZone');
    if (all?.length) return all;
  } catch {
    /* older runtime */
  }
  return ['UTC', 'America/New_York', 'America/Chicago', 'America/Denver',
          'America/Los_Angeles', 'Europe/London', 'Europe/Berlin',
          'Asia/Kolkata', 'Asia/Singapore', 'Australia/Sydney'];
}

/**
 * Parse a server value into an instant.
 *
 * A naive ISO string is treated as UTC — see the header. This is the one place
 * that decision is made, and it is why callers must never reach for
 * `new Date(x)` directly on an API value.
 */
export function parseServerDate(value: DateLike): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  if (typeof value === 'number') {
    // Heuristic with a wide margin: seconds-since-epoch for any plausible
    // application timestamp is ~1e9, milliseconds ~1e12. Anything below 1e11
    // would otherwise land in 1970 and read as "just after the epoch", which is
    // a wrong answer that looks like data rather than like an error.
    const d = new Date(value < 1e11 ? value * 1000 : value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const normalised = NAIVE_ISO.test(raw) && !HAS_OFFSET.test(raw)
    ? `${raw.replace(' ', 'T')}Z`
    : raw;

  const d = new Date(normalised);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** True when the server sent a wall-clock with no zone — useful for tests. */
export function isNaiveServerDate(value: DateLike): boolean {
  return typeof value === 'string' && NAIVE_ISO.test(value.trim())
    && !HAS_OFFSET.test(value.trim());
}

export interface FormatOptions {
  /** Append the zone (`CDT`, `GMT+5:30`) so the reading is unambiguous. */
  showZone?: boolean;
  /** Include seconds. Off by default — noise in most lists. */
  seconds?: boolean;
  /** What to render when there is no value. */
  fallback?: string;
  /** Override the zone for this call. Defaults to the viewer's. */
  timeZone?: string;
}

const fmt = (d: Date, o: Intl.DateTimeFormatOptions, tz: string) => {
  try {
    return new Intl.DateTimeFormat(undefined, { ...o, timeZone: tz }).format(d);
  } catch {
    // An invalid override must not blank the UI — fall back to the browser.
    return new Intl.DateTimeFormat(undefined, o).format(d);
  }
};

/** `10 Aug 2026, 03:19` — the default for tables and detail panes. */
export function formatDateTime(value: DateLike, opts: FormatOptions = {}): string {
  const d = parseServerDate(value);
  if (!d) return opts.fallback ?? '—';
  return fmt(d, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
    ...(opts.seconds ? { second: '2-digit' } : {}),
    ...(opts.showZone ? { timeZoneName: 'short' as const } : {}),
  }, opts.timeZone || userTimeZone());
}

/** `10 Aug 2026` */
export function formatDate(value: DateLike, opts: FormatOptions = {}): string {
  const d = parseServerDate(value);
  if (!d) return opts.fallback ?? '—';
  return fmt(d, { year: 'numeric', month: 'short', day: 'numeric' },
             opts.timeZone || userTimeZone());
}

/** `03:19:45` */
export function formatTime(value: DateLike, opts: FormatOptions = {}): string {
  const d = parseServerDate(value);
  if (!d) return opts.fallback ?? '—';
  return fmt(d, {
    hour: '2-digit', minute: '2-digit',
    ...(opts.seconds === false ? {} : { second: '2-digit' }),
    ...(opts.showZone ? { timeZoneName: 'short' as const } : {}),
  }, opts.timeZone || userTimeZone());
}

/**
 * The unambiguous form, for tooltips and exports: the instant in the viewer's
 * zone AND in UTC. Anyone comparing a screenshot against a log needs both, and
 * a support ticket that quotes only local time cannot be reconciled.
 */
export function formatAbsolute(value: DateLike, opts: FormatOptions = {}): string {
  const d = parseServerDate(value);
  if (!d) return opts.fallback ?? '—';
  const tz = opts.timeZone || userTimeZone();
  const local = fmt(d, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    timeZoneName: 'short',
  }, tz);
  return `${local}  ·  ${d.toISOString().replace('.000', '')} UTC`;
}

/** ISO-8601 in UTC — the form to put in a CSV or a URL. */
export function isoUTC(value: DateLike): string | null {
  const d = parseServerDate(value);
  return d ? d.toISOString() : null;
}

const REL: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 31536000], ['month', 2592000], ['week', 604800],
  ['day', 86400], ['hour', 3600], ['minute', 60], ['second', 1],
];

/** `3 minutes ago`, `in 2 days`. Zone-independent by construction. */
export function formatRelative(value: DateLike, opts: FormatOptions = {}): string {
  const d = parseServerDate(value);
  if (!d) return opts.fallback ?? '—';
  const diff = (d.getTime() - Date.now()) / 1000;
  if (Math.abs(diff) < 45) return 'just now';
  try {
    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
    for (const [unit, secs] of REL) {
      if (Math.abs(diff) >= secs || unit === 'second') {
        return rtf.format(Math.round(diff / secs), unit);
      }
    }
  } catch {
    /* fall through */
  }
  return formatDateTime(value, opts);
}

/**
 * `0.25s`, `45.0s`, `1m 12s`, `2h 5m` — the shared duration ladder
 * (lib/monitor/format), taken in seconds. A LENGTH, not an instant — no zone applies, which is
 * exactly why it belongs here: it is the function people reach for when they
 * would otherwise subtract two dates and format the result as a time of day.
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return '—';
  return fmtDurationSec(Math.max(0, Number(seconds)));
}

/** Elapsed between two server timestamps, in the same vocabulary. */
export function formatElapsed(from: DateLike, to: DateLike): string {
  const a = parseServerDate(from);
  const b = parseServerDate(to);
  if (!a || !b) return '—';
  return formatDuration((b.getTime() - a.getTime()) / 1000);
}

/** `10 Aug, 03:19 → 03:47` — collapses the date when both sides share one. */
export function formatRange(from: DateLike, to: DateLike, opts: FormatOptions = {}): string {
  const a = parseServerDate(from);
  const b = parseServerDate(to);
  if (!a) return opts.fallback ?? '—';
  if (!b) return `${formatDateTime(a, opts)} → …`;
  const tz = opts.timeZone || userTimeZone();
  const sameDay = formatDate(a, { timeZone: tz }) === formatDate(b, { timeZone: tz });
  return sameDay
    ? `${formatDateTime(a, { ...opts, timeZone: tz })} → ${formatTime(b, { ...opts, timeZone: tz, seconds: false })}`
    : `${formatDateTime(a, { ...opts, timeZone: tz })} → ${formatDateTime(b, { ...opts, timeZone: tz })}`;
}

/** The zone abbreviation on its own (`CDT`), for a column header or a legend. */
export function timeZoneLabel(at: DateLike = new Date(), tz?: string): string {
  const d = parseServerDate(at) || new Date();
  const zone = tz || userTimeZone();
  try {
    const part = new Intl.DateTimeFormat(undefined, { timeZone: zone, timeZoneName: 'short' })
      .formatToParts(d).find((p) => p.type === 'timeZoneName');
    return part?.value || zone;
  } catch {
    return zone;
  }
}

/**
 * `Date.parse` with the UTC assumption applied — returns epoch milliseconds, or
 * `NaN` for anything unparseable, so existing `Number.isNaN` guards, sorts and
 * subtractions keep working with no other change.
 *
 * `Date.parse` carries exactly the same defect as `new Date()`: a naive ISO
 * string is read as LOCAL time. Code that parsed first and formatted afterwards
 * therefore stayed wrong even once the formatter was correct, because the
 * damage was already done a line earlier. Twenty call sites looked like they
 * were only doing arithmetic and were quietly deciding a timezone.
 */
export function parseTime(value: DateLike): number {
  const d = parseServerDate(value);
  return d ? d.getTime() : NaN;
}
